/**
 * workshopCreateRegions — region-resolution helpers for the Create Workshop
 * drawer. Kept out of the drawer so the Location step shares them: both the
 * seeded-venue effect and the venue selector must resolve a venue's region the
 * SAME way, or one of them can set a region the operator never confirmed.
 */
import type { RegionNode } from '../../../../api/regions';

/** Find a region's name anywhere in the writable-regions tree ('' when absent). */
export function regionNameById(regions: RegionNode[], id: string): string {
  for (const r of regions) {
    if (r.id === id) return r.name;
    const found = regionNameById(r.children, id);
    if (found) return found;
  }
  return '';
}

/**
 * The region owning `venueId`, but ONLY when it's a region the viewer could
 * have picked themselves (i.e. it resolves in `regions`).
 *
 * The guard is the point of the helper: a venue option's `regionId` is the
 * venues API's GROUPING key, which is the `unassigned` pseudo-bucket for venues
 * with no operational region (see `api/venues.fetchVenues`). Adopting that as a
 * region would satisfy the "region is set" check while the selector still reads
 * "Select a region…", letting a workshop be created with a region the operator
 * never confirmed. Returns undefined instead, leaving Region required.
 */
export function selectableRegionForVenue(
  venues: ReadonlyArray<{ id: string; regionId: string }>,
  venueId: string,
  regions: RegionNode[],
): string | undefined {
  if (!venueId) return undefined;
  const owner = venues.find((v) => v.id === venueId)?.regionId;
  return owner && regionNameById(regions, owner) ? owner : undefined;
}

export { soleRegion } from '../../primitives/sole-region';
